import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function timeAgo(iso) {
  if (!iso) return '—'
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60)   return `${sec}s ago`
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  return `${Math.round(sec / 3600)}h ago`
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SKILL_COLORS = {
  pnr_ingest:       '#2196F3',
  ava_insights:     '#9C27B0',
  irop_event_push:  '#F44336',
  msp_dispatch:     '#FF9800',
  vuuoffer_push:    '#00BCD4',
  attestation_push: '#4CAF50',
  dispatch_status:  '#607D8B',
}

const CARRIER_COLORS = {
  B6: '#003087', DL: '#E01933', UA: '#005DAA', AA: '#0078D2', WN: '#304CB2',
}

const DISPATCH_STATUS_COLORS = {
  queued:          '#FF9800',
  worker_assigned: '#2196F3',
  en_route:        '#9C27B0',
  arrived:         '#00BCD4',
  completed:       '#4CAF50',
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ title, count, accent }) {
  return (
    <p className="text-gray-400 font-semibold uppercase tracking-wide text-xs mb-1.5 flex items-center gap-2">
      {title}
      {count != null && (
        <span
          className="px-1.5 py-0 rounded-full text-xs font-bold"
          style={{ background: (accent || '#9E9E9E') + '22', color: accent || '#9E9E9E' }}
        >
          {count}
        </span>
      )}
    </p>
  )
}

// ─── CloudMist thread ─────────────────────────────────────────────────────────

function CloudMistThread({ messages, pnrLocator }) {
  const [text, setText]     = useState('')
  const [sending, setSending] = useState(false)
  const [localMsgs, setLocalMsgs] = useState(messages)
  const bottomRef           = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [localMsgs.length])

  function send() {
    if (!text.trim() || sending) return
    setSending(true)
    fetch('/admin/cloudtraq_gds/message', {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
      body: JSON.stringify({ pnr_locator: pnrLocator, body: text.trim() }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setLocalMsgs(prev => [...prev, {
            id:         data.message_id,
            actor_type: 'operator',
            body:       text.trim(),
            sent_at:    new Date().toISOString(),
          }])
          setText('')
        }
        setSending(false)
      })
      .catch(() => setSending(false))
  }

  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden flex flex-col" style={{ minHeight: 200, maxHeight: 340 }}>
      <div className="bg-blue-50 border-b border-blue-100 px-3 py-2 flex items-center justify-between flex-shrink-0">
        <p className="text-xs font-semibold text-blue-700">☁ CloudMist — {pnrLocator}</p>
        <span className="text-xs text-gray-400">{localMsgs.length} messages</span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {localMsgs.length === 0 && (
          <p className="text-xs text-gray-400 text-center pt-4">No messages yet</p>
        )}
        {localMsgs.map(m => (
          <div
            key={m.id}
            className={`flex ${m.actor_type === 'operator' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] px-2 py-1 rounded text-xs ${m.actor_type === 'operator' ? 'bg-blue-600 text-white' : 'bg-gray-100 border border-gray-200 text-gray-800'}`}
            >
              {m.body}
              <span className={`block text-xs mt-0.5 ${m.actor_type === 'operator' ? 'text-blue-200' : 'text-gray-400'}`}>
                {m.actor_type} · {timeAgo(m.sent_at)}
              </span>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="flex border-t border-gray-200 flex-shrink-0">
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Message passenger…"
          className="flex-1 px-2 py-1.5 text-xs outline-none"
        />
        <button
          onClick={send}
          disabled={sending || !text.trim()}
          className="bg-blue-600 text-white text-xs px-3 hover:bg-blue-700 disabled:opacity-40 flex-shrink-0"
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PnrDetail({ pnr, skill_logs, dispatch_tasks, ava_conflicts, cloud_mist_messages, itinerary_ref, tenant_branding }) {
  const [analyzing, setAnalyzing] = useState(false)
  const [conflicts, setConflicts] = useState(ava_conflicts)
  const carrierColor = CARRIER_COLORS[pnr.carrier_code] || '#607D8B'
  const risk         = pnr.latest_attestation?.congestion_index
  const riskColor    = risk >= 0.7 ? '#F44336' : risk >= 0.4 ? '#FF9800' : '#4CAF50'

  function reanalyze() {
    if (analyzing) return
    setAnalyzing(true)
    fetch(`/admin/cloudtraq_gds/pnr/${pnr.pnr_locator}/ava`, {
      method:  'POST',
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data.ava_conflicts)) setConflicts(data.ava_conflicts)
        setAnalyzing(false)
      })
      .catch(() => setAnalyzing(false))
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono text-gray-900">{pnr.pnr_locator}</h1>
              {pnr.carrier_code && (
                <span className="text-xl font-bold font-mono" style={{ color: carrierColor }}>
                  {pnr.carrier_code}
                </span>
              )}
              {pnr.cabin_class && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono font-bold">
                  {pnr.cabin_class}
                </span>
              )}
              {risk != null && (
                <span className="text-sm font-bold tabular-nums" style={{ color: riskColor }}>
                  Risk: {risk.toFixed(2)}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {pnr.gds_locator && `Sabre GDS: ${pnr.gds_locator} · `}
              {pnr.ndc_order_id && `NDC: ${pnr.ndc_order_id} · `}
              {pnr.tenant_key && `Tenant: ${pnr.tenant_key}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={reanalyze}
              disabled={analyzing}
              className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-40"
            >
              {analyzing ? '…' : '🧠 Force AVA'}
            </button>
            <button onClick={() => router.reload()} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        <div className="flex gap-4" style={{ alignItems: 'flex-start' }}>

          {/* Left column — timeline + logs */}
          <div className="flex-1 min-w-0 space-y-4">

            {/* PNR summary */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
              <SectionHeader title="PNR Summary" />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                {[
                  ['PNR Locator',   pnr.pnr_locator],
                  ['Carrier Code',  pnr.carrier_code],
                  ['GDS Locator',   pnr.gds_locator],
                  ['NDC Order ID',  pnr.ndc_order_id],
                  ['Cabin Class',   pnr.cabin_class],
                  ['Carrier Tier',  pnr.carrier_tier],
                  ['Segment Type',  pnr.segment_type || 'airline'],
                  ['Itinerary Ref', pnr.itinerary_ref],
                  ['Tenant',        pnr.tenant_key],
                  ['Queue Session', pnr.queue_session_id ? `QS #${pnr.queue_session_id}` : null],
                  ['Anchor',        pnr.anchor_label],
                  ['Created',       pnr.created_at ? new Date(pnr.created_at).toLocaleDateString() : null],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label}>
                    <span className="text-gray-400">{label}</span>
                    <p className="font-semibold text-gray-800">{value}</p>
                  </div>
                ))}
              </div>
              {pnr.itinerary_ref && (
                <div className="mt-3">
                  <a
                    href={`/admin/cloudtraq_gds/itinerary/${pnr.itinerary_ref}`}
                    className="text-xs px-3 py-1.5 bg-teal-50 border border-teal-200 text-teal-700 rounded hover:bg-teal-100"
                  >
                    View Full Itinerary ({pnr.itinerary_ref}) →
                  </a>
                </div>
              )}
            </div>

            {/* Latest attestation */}
            {pnr.latest_attestation && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
                <SectionHeader title="Latest Attestation (channelSensor)" />
                <div className="grid grid-cols-3 gap-x-6 gap-y-2">
                  {Object.entries(pnr.latest_attestation).slice(0, 6).map(([k, v]) => (
                    <div key={k}>
                      <span className="text-gray-400 capitalize">{k.replace(/_/g, ' ')}</span>
                      <p className="font-semibold text-gray-800">{typeof v === 'number' ? v.toFixed(3) : String(v)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AVA Conflicts */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
              <div className="flex items-center justify-between mb-1.5">
                <SectionHeader title="AVA Conflicts" count={conflicts.length} accent="#9C27B0" />
                <button
                  onClick={reanalyze}
                  disabled={analyzing}
                  className="text-xs px-2 py-0.5 border border-purple-200 text-purple-600 rounded hover:bg-purple-50 disabled:opacity-40"
                >
                  {analyzing ? '…' : '↻ Re-analyze'}
                </button>
              </div>
              {conflicts.length === 0 ? (
                <p className="text-gray-400 italic">{analyzing ? 'Analyzing…' : 'No conflicts — journey looks clear'}</p>
              ) : (
                <div className="space-y-2">
                  {conflicts.map((c, i) => (
                    <div key={i} className="bg-red-50 border border-red-200 rounded px-2 py-1.5">
                      <p className="font-semibold text-red-700">{c.conflict_type}</p>
                      {c.description && <p className="text-red-600">{c.description}</p>}
                      {c.suggested_reschedule_at && (
                        <p className="text-red-500">Suggest: {formatAt(c.suggested_reschedule_at)}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* IBM ADK Events */}
            {pnr.ibm_events?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
                <SectionHeader title="IBM ADK Event Timeline" count={pnr.ibm_events.length} accent="#2196F3" />
                <div className="space-y-1.5 overflow-y-auto" style={{ maxHeight: 240 }}>
                  {[...pnr.ibm_events].reverse().map((evt, i) => (
                    <div key={i} className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded px-2 py-1.5">
                      <span className="font-mono text-blue-700 font-bold flex-shrink-0">{evt.event_type}</span>
                      <span className="text-gray-500 flex-1">{evt.occurred_at ? formatAt(evt.occurred_at) : '—'}</span>
                      {evt.conflict_type && <span className="text-red-500">{evt.conflict_type}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* GDS Skill Log */}
            {skill_logs?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
                <SectionHeader title="ADK Skill Invocations" count={skill_logs.length} accent="#9C27B0" />
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="py-1 pr-3 text-left text-gray-400 font-semibold">Skill</th>
                        <th className="py-1 pr-3 text-left text-gray-400 font-semibold">Status</th>
                        <th className="py-1 pr-3 text-right text-gray-400 font-semibold">Latency</th>
                        <th className="py-1 text-right text-gray-400 font-semibold">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skill_logs.map((log, i) => {
                        const color = SKILL_COLORS[log.skill] || '#9E9E9E'
                        return (
                          <tr key={log.id || i} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-1 pr-3">
                              <span className="font-bold" style={{ color }}>{log.skill}</span>
                            </td>
                            <td className="py-1 pr-3">
                              <span style={{ color: log.status === 'success' ? '#4CAF50' : log.status === 'error' ? '#F44336' : '#FF9800' }}>
                                {log.status || '—'}
                              </span>
                            </td>
                            <td className="py-1 pr-3 text-right tabular-nums text-gray-600">
                              {log.latency_ms != null ? `${log.latency_ms}ms` : '—'}
                            </td>
                            <td className="py-1 text-right text-gray-400">{timeAgo(log.occurred_at)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Dispatch tasks */}
            {dispatch_tasks?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs">
                <SectionHeader title="Dispatch Tasks" count={dispatch_tasks.length} accent="#FF9800" />
                <div className="space-y-2">
                  {dispatch_tasks.map(t => {
                    const color = DISPATCH_STATUS_COLORS[t.status] || '#9E9E9E'
                    return (
                      <div key={t.id} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                        <span className="font-mono font-bold text-gray-700">{t.dispatch_ref}</span>
                        <span
                          className="px-1.5 rounded text-xs font-semibold"
                          style={{ background: color + '22', color }}
                        >
                          {t.status}
                        </span>
                        {t.dispatch_type && <span className="text-gray-500">{t.dispatch_type}</span>}
                        {t.anchor_label && <span className="text-gray-400">{t.anchor_label}</span>}
                        {t.eta_minutes != null && <span className="text-purple-600 font-bold ml-auto">{t.eta_minutes}min ETA</span>}
                        <span className="text-gray-400">{timeAgo(t.created_at)}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right column — CloudMist chat */}
          <div className="flex-shrink-0" style={{ width: 320 }}>
            <CloudMistThread messages={cloud_mist_messages || []} pnrLocator={pnr.pnr_locator} />
          </div>
        </div>

        {/* Nav */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="text-blue-600 hover:underline">High Risk Candidates</Link>
          <Link href="/admin/cloudtraq_gds/skill_feed" className="text-blue-600 hover:underline">Skill Feed</Link>
          {itinerary_ref && (
            <a href={`/admin/cloudtraq_gds/itinerary/${itinerary_ref}`} className="text-blue-600 hover:underline">
              Full Itinerary →
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
